import { FeatureFlag } from '@ps/web-core-enums';
import type { Selector } from 'react-redux';
import { createSelector } from 'reselect';

import type { RootState, Slicer } from '@swf/core/store';
import { select } from '@swf/core/store';

import type { FilterVariables, Tournament } from '../../types/MTT';
import type { CommonQueryVariableOptions } from '../../utils/MTT';
import { filterMTTTournaments, mapStringToLocale } from '../../utils/MTT';

import type { PokerMultiTableTournamentsStateBase } from './types';

export const selectors = <T extends PokerMultiTableTournamentsStateBase>(slice: Slicer<T>) => ({
  isLoading: () => slice((state) => state.isLoading),
  getFilterVariables: () => slice((state) => state.activeFilters),
  getTournamentList(this: { getFilterVariables: Selector<RootState, FilterVariables> }) {
    return createSelector(slice, this.getFilterVariables, (state, filterVariables): Tournament[] =>
      filterMTTTournaments(filterVariables, Object.values(state.tournamentsData))
    );
  },
  getTournamentsCount: () => slice((state) => state.tournamentsCount),
  getSelectedTournament(this: { getSelectedTournamentId: Selector<RootState, number> }) {
    return createSelector(
      slice,
      this.getSelectedTournamentId,
      (state, selectedTournamentId): Tournament | undefined =>
        state.tournamentsData[selectedTournamentId]
    );
  },
  getTournamentById: () =>
    createSelector(
      slice,
      (_: RootState, tournamentId: number) => tournamentId,
      (state, tournamentId) => state.tournamentsData?.[tournamentId]
    ),
  getSelectedTournamentId: () => slice((state) => state.selectedTournamentId),
  getFiltersData() {
    return createSelector(
      slice,
      (state: RootState) => select.currency.getPlayMode(state),
      (state, playMode) => state.filtersData[playMode]
    );
  },
  getActiveFilters: () => slice((state) => state.activeFilters),
  getTableSortRule: () => slice((state) => state.tableSortRule),
  getTableFilters: () => slice((state) => state.tableFilters),
  getTableRowsCount: () => slice((state) => state.tableRowsCount),
  getConfigQueryVariables: () => () => ({}),
  getCommonVariables() {
    return createSelector(
      slice,
      (state: RootState): CommonQueryVariableOptions => ({
        country: select.account.getCountry(state),
        isEmbedded: select.device.isEmbedded(state),
        isPlayMoney: select.currency.isPlayMoney(state),
        locale: mapStringToLocale(select.locale.get(state)),
        siteName: select.cms.getSiteName(state)
      }),
      (_, variables) => variables
    );
  },
  isSharedLiquidityAvailable() {
    return createSelector(
      slice,
      (state: RootState) => ({
        isAgeVerified: select.account.isAgeVerified(state),
        isAuthVerified: select.account.isAuthVerified(state),
        isPlayMoney: select.currency.isPlayMoney(state),
        isAuthenticated: select.session.isAuthenticated(state),
        isSharedLiquidityEnabled: select.feature.check(
          state,
          FeatureFlag.PKRRegulatoryAuthAgeVerification
        )
      }),
      (_, variables) => {
        const {
          isAgeVerified,
          isAuthenticated,
          isAuthVerified,
          isPlayMoney,
          isSharedLiquidityEnabled
        } = variables;

        return (
          !isSharedLiquidityEnabled ||
          !isAuthenticated ||
          isPlayMoney ||
          (isAuthVerified && isAgeVerified)
        );
      }
    );
  },
  getTournamentsCurrency: () => slice((state) => state.tournamentsCurrency)
});
